import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import _ from "lodash";
import { Subject } from "./Subject";
import { disciplineAsync } from "../discipline/disciplineSlice";
import {
  MAX_SUBJECTS,
  ORG_CLASSIFICATION,
  SERP_PLATFORMS,
} from "../../../config";
import "./subject.scss";

export function SubjectContainer(props) {
  const disciplineCount = useSelector((state) => state.subject.disciplineCount);
  const k12DisciplineCount = useSelector(
    (state) => state.subject.k12DisciplineCount
  );
  const isHigheredDisCountReceived = useSelector(
    (state) => state.subject.isHigheredDisCountReceived
  );
  const isK12ResultDisCountReceived = useSelector(
    (state) => state.subject.isK12ResultDisCountReceived
  );
  const disciplineList = useSelector(
    (state) => state.discipline.disciplineList
  );
  const orgClassification = useSelector(
    (state) => state.user.orgClassification
  );
  const currentResultPage = useSelector(
    (state) => state.searchResult.currentResultPage
  );
  const selectedPlatform = useSelector(
    (state) => state.platform.selectedPlatform
  );
  const searchResult = useSelector((state) => state.searchResult.searchResult);
  const k12SearchResult = useSelector(
    (state) => state.searchResult.k12SearchResult
  );
  const selectedSubjectDisplayName = useSelector(
    (state) => state.subject.selectedSubjectDisplayName
  );
  const [topSubjects, setTopSubjects] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [isShowMore, setIsShowMore] = useState(true);
  const [allDisciplineCount, setAllDisciplineCount] = useState(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const hasDisciplineFilter = Boolean(queryParams.get("disc"));

  const getSelectedDisciplineName = () => {
    if (selectedSubjectDisplayName) {
      return selectedSubjectDisplayName;
    }

    const discParam = queryParams.get("disc");
    if (!discParam || !disciplineList?._embedded?.disciplineCategories) {
      return null;
    }

    const discCode = decodeURIComponent(discParam).split("|")[0];
    for (const category of disciplineList._embedded.disciplineCategories) {
      for (const primaryDiscipline of category.primaryDisciplines) {
        const match = _.find(
          primaryDiscipline.secondaryDisciplines,
          (secondaryDiscipline) => secondaryDiscipline.code === discCode
        );
        if (match) {
          return primaryDiscipline.name;
        }
      }
    }

    return null;
  };

  const shouldMergeK12Counts = () =>
    orgClassification !== ORG_CLASSIFICATION.HIGHER_ED &&
    currentResultPage === 1 &&
    (!selectedPlatform || selectedPlatform === SERP_PLATFORMS.REVEL.type);

  const getMergedSearchCount = () => {
    if (!searchResult || searchResult.count == null) {
      return null;
    }

    if (
      shouldMergeK12Counts() &&
      k12SearchResult &&
      k12SearchResult.count != null
    ) {
      return searchResult.count + k12SearchResult.count;
    }

    return searchResult.count;
  };

  const applySelectedDisciplineCount = (subjects) => {
    const selectedDisciplineName = getSelectedDisciplineName();
    const mergedSearchCount = getMergedSearchCount();

    if (
      !hasDisciplineFilter ||
      !selectedDisciplineName ||
      mergedSearchCount == null
    ) {
      return subjects;
    }

    return subjects.map((category) => ({
      ...category,
      items: category.items.map((item) =>
        item.key === selectedDisciplineName
          ? { ...item, count: mergedSearchCount }
          : item
      ),
    }));
  };

  useEffect(() => {
    if (disciplineCount && !disciplineList) {
      dispatch(disciplineAsync());
    } else {
      populateSubjects(allDisciplineCount, disciplineList);
    }
  }, [
    disciplineCount,
    disciplineList,
    allDisciplineCount,
    searchResult,
    k12SearchResult,
    selectedSubjectDisplayName,
    location.search,
    selectedPlatform,
    orgClassification,
    currentResultPage,
  ]);

  useEffect(() => {
    if (
      shouldMergeK12Counts() &&
      isHigheredDisCountReceived &&
      isK12ResultDisCountReceived
    ) {
      let tempAllDisciplineCount = { count: 0, searchResults: [] };
      if (disciplineCount !== "") {
        tempAllDisciplineCount = JSON.parse(JSON.stringify(disciplineCount));
      }

      const selectedDisciplineName = getSelectedDisciplineName();

      k12DisciplineCount &&
        k12DisciplineCount.searchResults &&
        k12DisciplineCount.searchResults.forEach((countItem) => {
          if (
            hasDisciplineFilter &&
            selectedDisciplineName &&
            countItem.key === selectedDisciplineName
          ) {
            return;
          }

          const tempItem = tempAllDisciplineCount.searchResults.findIndex(
            (element) => element.key === countItem.key
          );
          if (tempItem > -1) {
            tempAllDisciplineCount.searchResults[tempItem].count =
              countItem.count + disciplineCount.searchResults[tempItem].count;
          } else {
            tempAllDisciplineCount.searchResults.push(countItem);
          }
        });
      setAllDisciplineCount(tempAllDisciplineCount);
    } else {
      setAllDisciplineCount(disciplineCount);
    }
  }, [
    isHigheredDisCountReceived,
    isK12ResultDisCountReceived,
    orgClassification,
    disciplineCount,
    k12DisciplineCount,
    currentResultPage,
    selectedPlatform,
    selectedSubjectDisplayName,
    location.search,
    disciplineList,
  ]);

  const populateSubjects = (disciplineCountItems, discipline) => {
    if (disciplineCountItems && discipline) {
      let subjectItems = disciplineCountItems.searchResults.map(
        (disciplineCountItem) => {
          for (const categoryItem of discipline._embedded
            .disciplineCategories) {
            let subjectItem = null;
            subjectItem = _.find(
              categoryItem.primaryDisciplines,
              function (category) {
                return category.name === disciplineCountItem.key;
              }
            );
            if (subjectItem) {
              return {
                ...disciplineCountItem,
                category: categoryItem.name,
              };
            }
          }
          return null;
        }
      );

      const formatSubjectData = (subjectItems) => {
        // Filter out null values before processing
        const validSubjectItems = subjectItems.filter((item) => item !== null);
        let categories = _.unionBy(validSubjectItems, "category").map(
          (e) => e.category
        );
        let subjects = categories.map((category) => {
          let subject = {};
          subject.category = category;
          subject.items = _.filter(validSubjectItems, function (item) {
            return category === item.category;
          });
          return subject;
        });
        return subjects;
      };

      setTopSubjects(
        applySelectedDisciplineCount(
          formatSubjectData(_.take(subjectItems, MAX_SUBJECTS))
        )
      );
      setAllSubjects(
        applySelectedDisciplineCount(formatSubjectData(subjectItems))
      );
    } else {
      setTopSubjects([]);
      setAllSubjects([]);
    }
  };

  return (
    <React.Fragment>
      {topSubjects && topSubjects.length > 0 && isShowMore && (
        <Subject subjectItems={topSubjects} />
      )}
      {allSubjects && allSubjects.length > 0 && !isShowMore && (
        <Subject subjectItems={allSubjects} />
      )}
      {disciplineList &&
        allDisciplineCount &&
        allDisciplineCount.searchResults &&
        allDisciplineCount.searchResults.length > MAX_SUBJECTS && (
          <button
            onClick={() => setIsShowMore((prevIsShowMore) => !prevIsShowMore)}
            className="show-link text-btn-link"
            aria-expanded={!isShowMore}
            aria-live="assertive"
            aria-atomic="true"
            aria-relevant="all">
            {isShowMore ? "Show more +" : "Show less -"}
          </button>
        )}
    </React.Fragment>
  );
}
